import React, { useState, useEffect, useCallback } from "react";
import { StyleSheet, View, ScrollView, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";

import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@components/firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { getDatabaseMigrations, runDatabaseMigrations } from "@/services/firebase/migration-functions";
import { IMigrationAdminItem } from "common247";

/**
 * Administrative screen for inspecting and triggering database schema migrations.
 * Restricted strictly to administrator accounts.
 */
export default function MigrationAdminScreen(): React.JSX.Element {
  useAuth(true);
  const services = useFirebase();

  const [migrations, setMigrations] = useState<IMigrationAdminItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [running, setRunning] = useState<boolean>(false);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const fetchMigrations = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const response = await getDatabaseMigrations(services);
      if (response.status) {
        setMigrations(response.migrations || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to load database migrations.");
      }
    } catch (err: any) {
      console.error("Error fetching database migrations:", err);
      CustomAlert.alert("Error", err?.message || "Unexpected error while fetching migrations.");
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => {
    fetchMigrations();
  }, [fetchMigrations]);

  const handleRunMigrations = () => {
    if (!services) return;
    const pendingCount = migrations.filter((m) => m.status === "pending" || m.status === "failed").length;

    CustomAlert.alert(
      "Execute Migrations",
      `Are you sure you want to run database migrations? There are ${pendingCount} pending/failed migration(s).`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Run Now",
          style: "default",
          onPress: async () => {
            setRunning(true);
            try {
              const res = await runDatabaseMigrations(services);
              if (res.status) {
                CustomAlert.alert("Migration Finished", res.message);
                await fetchMigrations();
              } else {
                CustomAlert.alert("Migration Failed", res.message || res.error || "Unknown failure");
                await fetchMigrations();
              }
            } catch (err: any) {
              console.error("Error executing migrations:", err);
              CustomAlert.alert("Error", err?.message || "Failed to execute migrations.");
            } finally {
              setRunning(false);
            }
          },
        },
      ]
    );
  };

  const totalCount = migrations.length;
  const completedCount = migrations.filter((m) => m.status === "complete").length;
  const pendingCount = migrations.filter((m) => m.status === "pending").length;
  const failedCount = migrations.filter((m) => m.status === "failed").length;

  return (
    <ThemedScreen
      title="Database Migrations"
      loading={loading && !running}
      rightIcon={{
        name: "refresh",
        onPress: fetchMigrations,
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
          DATABASE SCHEMA & DATA MIGRATION ENGINE
        </ThemedText>

        {/* Telemetry Overview Panel */}
        <ThemedPanel padding={14} elevation="low" style={styles.metricsPanel}>
          <View style={styles.metricsGrid}>
            <View style={[styles.metricBox, { backgroundColor: badgeBg, borderColor }]}>
              <ThemedText type="title" style={styles.metricValue}>
                {totalCount}
              </ThemedText>
              <ThemedText style={[styles.metricLabel, { color: textMuted }]}>
                Total Registered
              </ThemedText>
            </View>

            <View style={[styles.metricBox, { backgroundColor: "#E8F8F5", borderColor: "#A2D9CE" }]}>
              <ThemedText type="title" style={[styles.metricValue, { color: "#27AE60" }]}>
                {completedCount}
              </ThemedText>
              <ThemedText style={[styles.metricLabel, { color: "#1E8449" }]}>
                Completed
              </ThemedText>
            </View>

            <View style={[styles.metricBox, { backgroundColor: "#FEF9E7", borderColor: "#FAD7A0" }]}>
              <ThemedText type="title" style={[styles.metricValue, { color: "#D4AC0D" }]}>
                {pendingCount}
              </ThemedText>
              <ThemedText style={[styles.metricLabel, { color: "#B7950B" }]}>
                Pending
              </ThemedText>
            </View>

            <View style={[styles.metricBox, { backgroundColor: failedCount > 0 ? "#FDEDEC" : badgeBg, borderColor: failedCount > 0 ? "#F5B7B1" : borderColor }]}>
              <ThemedText type="title" style={[styles.metricValue, { color: failedCount > 0 ? "#E74C3C" : textMuted }]}>
                {failedCount}
              </ThemedText>
              <ThemedText style={[styles.metricLabel, { color: failedCount > 0 ? "#C0392B" : textMuted }]}>
                Failed
              </ThemedText>
            </View>
          </View>

          {/* Action Trigger Row */}
          <View style={styles.actionRow}>
            <ThemedButton
              title={running ? "Executing Migrations..." : "Run Migrations"}
              variant="primary"
              disabled={running || (pendingCount === 0 && failedCount === 0)}
              onPress={handleRunMigrations}
              style={{ flex: 1 }}
            />
            {running && (
              <ActivityIndicator
                size="small"
                color={tintColor}
                style={{ marginLeft: 12 }}
              />
            )}
          </View>
        </ThemedPanel>

        {/* Migrations Timeline List */}
        <ThemedText type="defaultSemiBold" style={{ fontSize: 16, marginBottom: 8 }}>
          Migration Registry & History
        </ThemedText>

        {migrations.length === 0 && !loading && (
          <ThemedPanel padding={20} elevation="low" style={{ alignItems: "center" }}>
            <Ionicons name="documents-outline" size={32} color={textMuted} />
            <ThemedText style={{ color: textMuted, marginTop: 8 }}>
              No database migrations registered in system.
            </ThemedText>
          </ThemedPanel>
        )}

        {migrations.map((item) => {
          const isComplete = item.status === "complete";
          const isFailed = item.status === "failed";
          const isPending = item.status === "pending";

          const statusColor = isComplete ? "#27AE60" : isFailed ? "#E74C3C" : "#E67E22";
          const statusBg = isComplete ? "#E8F8F5" : isFailed ? "#FDEDEC" : "#FEF9E7";
          const statusText = isComplete ? "Applied" : isFailed ? "Failed" : "Pending";
          const statusIcon = isComplete ? "checkmark-circle" : isFailed ? "alert-circle" : "time";

          return (
            <ThemedPanel
              key={item.id}
              padding={14}
              elevation="low"
              style={[styles.card, { borderColor }]}
            >
              <View style={styles.cardHeader}>
                <View style={styles.idContainer}>
                  <View style={[styles.idBadge, { backgroundColor: badgeBg, borderColor }]}>
                    <ThemedText type="code" style={{ fontSize: 13, fontWeight: "700" }}>
                      MIG-{item.id}
                    </ThemedText>
                  </View>
                  <ThemedText type="defaultSemiBold" style={styles.cardTitle}>
                    {item.name}
                  </ThemedText>
                </View>

                <View style={[styles.statusBadge, { backgroundColor: statusBg, borderColor: statusColor }]}>
                  <Ionicons name={statusIcon} size={13} color={statusColor} style={{ marginRight: 4 }} />
                  <ThemedText style={[styles.statusBadgeText, { color: statusColor }]}>
                    {statusText}
                  </ThemedText>
                </View>
              </View>

              <View style={styles.metaRow}>
                <Ionicons name="calendar-outline" size={13} color={textMuted} style={{ marginRight: 4 }} />
                <ThemedText style={[styles.metaText, { color: textMuted }]}>
                  {item.appliedAt ? `Applied: ${new Date(item.appliedAt).toLocaleString()}` : "Not applied yet"}
                </ThemedText>
              </View>

              {Boolean(item.reason) && (
                <View style={[styles.reasonBox, { backgroundColor: badgeBg, borderColor }]}>
                  <Ionicons name="information-circle-outline" size={14} color={tintColor} style={{ marginRight: 6, marginTop: 1 }} />
                  <ThemedText style={styles.reasonText}>
                    {item.reason}
                  </ThemedText>
                </View>
              )}

              {item.error && (
                <View style={styles.errorBox}>
                  <ThemedText style={styles.errorText}>
                    Error: {item.error}
                  </ThemedText>
                </View>
              )}
            </ThemedPanel>
          );
        })}
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    paddingBottom: 40,
  },
  metricsPanel: {
    marginBottom: 16,
  },
  metricsGrid: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 14,
  },
  metricBox: {
    flex: 1,
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  metricValue: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 2,
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  actionRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  card: {
    marginBottom: 10,
    borderRadius: 4,
    borderWidth: 1,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  idContainer: {
    flex: 1,
    marginRight: 8,
  },
  idBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    borderWidth: 1,
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 14,
    lineHeight: 18,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  metaText: {
    fontSize: 12,
  },
  reasonBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    marginTop: 6,
    marginBottom: 4,
  },
  reasonText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
  },
  errorBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: "#FDEDEC",
    borderColor: "#F5B7B1",
    borderWidth: 1,
    borderRadius: 4,
  },
  errorText: {
    color: "#C0392B",
    fontSize: 12,
    fontFamily: "monospace",
  },
});
